import {Icon} from '@/app/components/Icon';
import {useRef, useState} from 'react';
import {Button} from '@/components/app/button';
import {cn} from '@/lib/utils';
import {Form} from '../../../app/components/Form';
import {useSendFeedbackMutation} from '../../../app/services/identitiesApi';
import {toastError, toastSuccess} from '../../../app/toast';
import styles from './Feedback.module.css';
export enum FeedbackType {
  Bug,
  Feedback,
  Message,
}
export interface FeedbackFormFieldValues {
  message: string;
  screenshot: boolean;
  type: FeedbackType;
}
export interface FeedbackProps {
  className?: string;
}
export const Feedback = ({className}: FeedbackProps) => {
  const form = useRef<Form<FeedbackFormFieldValues>>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [sendFeedback] = useSendFeedbackMutation();
  return (
    <div className={cn(styles.feedback, className)}>
      {!formVisible && (
        <div className={styles.buttonWrapper}>
          <Button className={styles.button} variant="success" onClick={() => setFormVisible(true)} type="button">
            {'Feedback'}
          </Button>
        </div>
      )}
      {formVisible && (
        <div className={cn(styles.formWrapper, 'shadow-xl')}>
          <header className="flex items-center gap-2 mb-4">
            <h6 className="flex-1 text-base my-0">Give us feedback</h6>
            <Button
              className="bg-transparent rounded-full"
              onClick={() => {
                setFormVisible(false);
                form.current?.reset();
              }}
              type="button"
              startIcon={<Icon icon={'times'} />}
              aria-label={'Close'}
              size="icon"
            />
          </header>
          <Form<FeedbackFormFieldValues>
            defaultValues={{
              screenshot: true,
              type: FeedbackType.Bug,
            }}
            fields={[
              {
                label: 'Type',
                name: 'type',
                rules: {
                  required: true,
                },
                type: 'dropdown',
                typeOptions: {
                  options: [
                    {
                      label: 'Issue',
                      value: FeedbackType.Bug,
                    },
                    {
                      label: 'Suggestion',
                      value: FeedbackType.Feedback,
                    },
                    {
                      label: 'Other',
                      value: FeedbackType.Message,
                    },
                  ],
                  optionLabel: 'label',
                  optionValue: 'value',
                },
              },
              {
                label: 'Message',
                name: 'message',
                rules: {
                  maxLength: 64,
                  required: true,
                },
                type: 'textarea',
                typeOptions: {
                  autoFocus: true,
                  maxLength: 64,
                  rows: 6,
                },
              },
              {
                label: 'Add Screenshot',
                name: 'screenshot',
                type: 'checkbox',
                typeOptions: {
                  className: 'ml-4',
                },
              },
            ]}
            onSubmit={onSubmit}
            ref={form}
            submitButton={{
              children: 'Send Feedback',
            }}
          />
        </div>
      )}
    </div>
  );
  async function onSubmit(fieldValues: FeedbackFormFieldValues) {
    try {
      let screenshot = '';
      if (fieldValues.screenshot) {
        const {default: html2canvas} = await import('html2canvas');
        const canvas = await html2canvas(document.body, {
          ignoreElements: (element) => element.classList.contains(styles.feedback),
        });
        screenshot = canvas.toDataURL();
      }
      await sendFeedback({
        feedbackType: fieldValues.type,
        message: fieldValues.message,
        screenshot,
      }).unwrap();
      toastSuccess('Feedback is sent successfully.');
    } catch (e) {
      toastError(e);
    } finally {
      setFormVisible(false);
      form.current?.reset();
    }
  }
};
